import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, Zap, Network, ArrowRight, CheckCircle, TrendingUp, Users, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { OptimizedImage } from "@/components/OptimizedImage";
import aiEmailImage from "@/assets/ai-email-automation.jpg";
import erpFlowImage from "@/assets/erp-integration-flow.jpg";
import multiPlatformImage from "@/assets/multi-platform-integration.jpg";
import { openCalendlyDemo } from "@/lib/analytics";

const ERPIntegrations = () => {
  const integrationMethods = [
    {
      icon: <Mail className="w-8 h-8 text-white" />,
      title: "AI Email Prevention",
      subtitle: "Exception Prevention",
      description: "Conversational AI that resolves AP exceptions upstream — directly with suppliers via email, with real-time ERP context. 90% of exceptions resolved without AP staff involvement.",
      image: aiEmailImage,
      stats: ["Up to 90% Exception Prevention", "Immediate Responses", "Zero Training Required"],
      badge: "AI-First",
      badgeColor: "bg-accent text-accent-foreground",
      link: "/technology/ai-email-prevention"
    },
    {
      icon: <Network className="w-8 h-8 text-white" />,
      title: "Native ERP Integration",
      subtitle: "Enterprise Ready",
      description: "Prebuilt, EZ Cloud-maintained integrations for Oracle EBS + Fusion, NetSuite, SAP, Dynamics 365, and Yardi Voyager. Bi-directional sync, no middleware.",
      image: erpFlowImage,
      stats: ["Real-Time Sync", "99.9% Uptime SLA", "Enterprise Security"],
      badge: "Enterprise",
      badgeColor: "bg-primary text-primary-foreground",
      link: "/technology/native-erp"
    },
    {
      icon: <Zap className="w-8 h-8 text-white" />,
      title: "Multi-Channel Capture", 
      subtitle: "Complete Coverage",
      description: "Supplier portals, mobile apps, email automation, and document scanning. Every invoice captured automatically.",
      image: multiPlatformImage,
      stats: ["6 Capture Methods", "100% Invoice Capture", "Mobile Optimized"],
      badge: "Complete",
      badgeColor: "bg-secondary text-secondary-foreground",
      link: "/technology/multi-platform"
    }
  ];

  return (
    <section id="integrations" className="py-20 bg-hero-gradient text-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-primary/10"></div>
      <div className="absolute top-0 left-0 w-96 h-96 bg-accent/20 rounded-full blur-3xl -translate-x-48 -translate-y-48"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/20 rounded-full blur-3xl translate-x-48 translate-y-48"></div>
      
      <div className="container mx-auto px-4 relative">
        {/* Header Section */}
        <div className="text-center mb-20">
          <h2 className="text-4xl lg:text-6xl font-bold mb-6">
            Three AI capabilities
            <span className="block text-accent">behind the platform</span>
          </h2>
          <p className="text-xl lg:text-2xl mb-8 text-white/90 max-w-4xl mx-auto">
            Together they prevent AP exceptions upstream, sync data to your ERP in real time, and capture invoices from every channel — so your AP team works on what matters.
          </p>
          
          {/* Social Proof Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-4xl mx-auto mb-12">
            <div className="text-center">
              <div className="text-3xl font-bold text-accent mb-2">25+</div>
              <div className="text-sm text-white/80">Years in Oracle AP</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-accent mb-2">71%</div>
              <div className="text-sm text-white/80">Faster AP Processing</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-accent mb-2">88-92%</div>
              <div className="text-sm text-white/80">Touchless Processing</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-accent mb-2">24/7</div>
              <div className="text-sm text-white/80">AI Monitoring</div>
            </div>
          </div>
        </div>

        {/* Integration Methods */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {integrationMethods.map((method, index) => (
            <div
              key={index}
              className="group relative flex flex-col overflow-hidden rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-all duration-500 hover:-translate-y-2"
            >
              {/* Background Image */}
              <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity duration-500">
                <OptimizedImage 
                  src={method.image} 
                  alt=""
                  width={1200}
                  height={800}
                  loading={index === 0 ? "eager" : "lazy"}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Content */}
              <div className="relative p-8 flex flex-col flex-1">
                {/* Badge */}
                <div className="flex items-center justify-between mb-6">
                  <Badge className={`${method.badgeColor} text-sm px-3 py-1`}>
                    {method.badge}
                  </Badge>
                  <div className="p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                    {method.icon}
                  </div>
                </div>
                
                {/* Title & Subtitle */}
                <div className="mb-4">
                  <h3 className="text-2xl font-bold text-white mb-2">{method.title}</h3>
                  <p className="text-accent font-semibold">{method.subtitle}</p>
                </div>
                
                {/* Description */}
                <p className="text-white/90 leading-relaxed mb-6">
                  {method.description}
                </p>
                
                {/* Stats */}
                <div className="space-y-3 mb-8">
                  {method.stats.map((stat, statIndex) => (
                    <div key={statIndex} className="flex items-center text-sm text-white/90">
                      <CheckCircle className="w-4 h-4 text-accent mr-2 flex-shrink-0" />
                      {stat}
                    </div>
                  ))}
                </div>
                
                {/* CTA */}
              <Button
                asChild
                size="default"
                variant="outline-on-dark"
                className="w-full px-6 py-3 text-sm mt-auto"
              >
                <Link to={method.link}>
                  Learn more about {method.title}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              </div>
            </div>
          ))}
        </div>


        {/* CTA Section */}
        <div className="text-center">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-3xl font-bold text-white mb-6">
              Ready to see the architecture in action?
            </h3>
            <p className="text-xl text-white/90 mb-8">
              30 minutes, your invoice sample, your ERP — and we walk a real exception from email to ERP posting. See the platform end-to-end before you commit to anything.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-lg px-8 py-4 bg-accent text-accent-foreground hover:bg-accent/90"
                onClick={() => openCalendlyDemo()}
              >
                <Clock className="w-5 h-5 mr-2" />
                Book Live Demo
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline-on-dark"
                className="text-lg px-8 py-4"
              >
                <Link to="/integrations">
                  Explore All Integrations
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
            
            <p className="text-sm text-white/70 mt-4">
              Typical implementation 6–12 weeks depending on ERP complexity · SOC 2 Type II · SSO supported
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ERPIntegrations;